import { assign } from 'xstate';
import logger from '../../../logger';
import { fetchNoteFromCloud, saveNoteCloud, createNote } from '../../cloud/notes';
import {
  writeNoteToClientDatabase,
  writeAreaOfFocus,
  writePendingWrite,
  readPendingWrites,
  deletePendingWrite,
} from '../../offline';
import { addNoteToAreaOfFocusCollection, removeNoteFromAreaOfFocusCollection } from '../../cloud/areasOfFocusCollections';
import { trashNoteInCloud } from '../../cloud/trash';
import { openNoteHelper } from '../../../utils/noteHelpers';
import { notebookTreeHelper, setLastSelectedItemIdInCollection } from '../../../utils/helpers';
import interactionLedger from '../../../utils/interactionLedger';
import type { AppStateContext, AppStateEvent } from '../../../types/appState';
import type { Note } from '../../../types/notes';
import type { AOFCollection } from '../../../types/areasOfFocusCollections';

// ---------------------------------------------------------------------------
// Service functions — async work extracted from inline lambdas
// ---------------------------------------------------------------------------

async function createNoteService(context: AppStateContext, event: any) {
  const { defaultNotebook, notebooks, newNoteEntityObject, selectedAreaOfFocus: ctxAreaOfFocus, selectedNotebookId } = context;
  const notebookIdToUse = selectedNotebookId === 'all_notes'
    ? defaultNotebook?.notebook_id
    : selectedNotebookId;
  const {
    noteObject: rawNoteObject = { ...newNoteEntityObject, notebook_id: notebookIdToUse },
    selectedAreaOfFocus = ctxAreaOfFocus,
    openNotesLength = ctxAreaOfFocus?.order_of_items?.length ?? 0,
    isSelectedAreaOfFocus = false,
  } = event.data || {};

  // Assign a client-side ID so the note can be written to IndexedDB and queued
  // in pendingWrites if the API call fails or we're offline.
  const noteObject = rawNoteObject.note_id
    ? rawNoteObject
    : { ...rawNoteObject, note_id: crypto.randomUUID() };

  // Ensure notebook_id is a notebook the client knows about. If it's missing or
  // points to an unknown notebook, fall back to the default notebook so the API
  // doesn't assign a random UUID that has no match in the notebooks context.
  // Only correct when we actually have a valid fallback — never set to undefined.
  const fallbackNotebookId = defaultNotebook?.notebook_id;
  if (fallbackNotebookId && (!noteObject.notebook_id || (notebooks && !notebooks[noteObject.notebook_id]))) {
    (noteObject as any).notebook_id = fallbackNotebookId;
    logger.info(
      { note_id: noteObject.note_id, notebook_id: fallbackNotebookId },
      'createNoteService: corrected missing/unknown notebook_id to default notebook'
    );
  }

  if (!navigator.onLine) {
    logger.warn({ note_id: noteObject.note_id }, 'createNoteService: offline — queuing note creation');
    await writePendingWrite({ id: noteObject.note_id, type: 'createNote', payload: { noteObject, selectedAreasOfFocusCollection: selectedAreaOfFocus, openNotesLength, isSelectedAreaOfFocus } });
    await writeNoteToClientDatabase({ noteObject });
    return { note: noteObject, selectedAreaOfFocus, isSelectedAreaOfFocus, openNotesLength, queued: true };
  }

  interactionLedger.log(
    'noteCreate_start',
    'stateMachine',
    { note_id: noteObject.note_id, notebook_id: noteObject.notebook_id, aof_id: selectedAreaOfFocus?.area_of_focus_collection_id ?? null },
    `createNote: sending POST /note for ${noteObject.note_id}`
  );

  try {
    const createdNote = await createNote({
      noteObject,
      selectedAreasOfFocusCollection: selectedAreaOfFocus,
      openNotesLength,
      isSelectedAreaOfFocus,
    });

    const updatedAreaOfFocus = createdNote.area_of_focus_collection || null;

    interactionLedger.log(
      'noteCreate_apiResponse',
      'stateMachine',
      {
        note_id: createdNote.note?.note_id,
        trash: createdNote.note?.trash,
        notebook_id: createdNote.note?.notebook_id,
        note_body_length: createdNote.note?.note_body?.length ?? 0,
        has_aof: !!updatedAreaOfFocus,
        aof_order_of_items: updatedAreaOfFocus?.order_of_items ?? null,
      },
      `POST /note response: note=${createdNote.note?.note_id} trash=${createdNote.note?.trash} aof=${!!updatedAreaOfFocus}`
    );

    // If the API assigned a notebook_id we don't recognise, correct it — but
    // only when we have a valid fallback. Never overwrite with undefined, which
    // would break the "Note Repaired" recovery path in the notes panel.
    const returnedNotebookId = createdNote.note.notebook_id;
    const correctedNotebookId = defaultNotebook?.notebook_id;
    if (correctedNotebookId && (!returnedNotebookId || (notebooks && !notebooks[returnedNotebookId]))) {
      createdNote.note.notebook_id = correctedNotebookId;
      logger.warn(
        { note_id: createdNote.note.note_id, bad_notebook_id: returnedNotebookId, corrected_to: correctedNotebookId },
        'createNoteService: API returned unknown notebook_id — correcting and re-saving'
      );
      interactionLedger.log(
        'noteCreate_notebookCorrection',
        'stateMachine',
        { note_id: createdNote.note.note_id, bad: returnedNotebookId, corrected_to: correctedNotebookId },
        `Correcting notebook_id: ${returnedNotebookId} → ${correctedNotebookId}`
      );
      try {
        const savedNote = await saveNoteCloud({ noteObject: createdNote.note });
        return { note: savedNote, selectedAreaOfFocus, isSelectedAreaOfFocus, openNotesLength, queued: false, updatedAreaOfFocus };
      } catch (saveError: any) {
        logger.warn({ note_id: createdNote.note.note_id, error: saveError?.message }, 'createNoteService: failed to correct notebook_id via save — using local note');
        return { note: createdNote.note, selectedAreaOfFocus, isSelectedAreaOfFocus, openNotesLength, queued: false, updatedAreaOfFocus };
      }
    }

    interactionLedger.log(
      'noteCreate_returning',
      'stateMachine',
      { note_id: createdNote.note.note_id, queued: false, has_aof: !!updatedAreaOfFocus },
      `createNoteService returning note ${createdNote.note.note_id} (not queued)`
    );
    return { note: createdNote.note, selectedAreaOfFocus, isSelectedAreaOfFocus, openNotesLength, queued: false, updatedAreaOfFocus };
  } catch (error: any) {
    logger.warn({ note_id: noteObject.note_id, error: error?.message }, 'createNoteService: API error — queuing note creation');
    interactionLedger.log(
      'noteCreate_apiError',
      'stateMachine',
      { note_id: noteObject.note_id, error: error?.message, errorType: error?.errorType },
      `POST /note failed — queuing: ${error?.message}`
    );
    await writePendingWrite({ id: noteObject.note_id, type: 'createNote', payload: { noteObject, selectedAreasOfFocusCollection: selectedAreaOfFocus, openNotesLength, isSelectedAreaOfFocus } });
    await writeNoteToClientDatabase({ noteObject });
    return { note: noteObject, selectedAreaOfFocus, isSelectedAreaOfFocus, openNotesLength, queued: true };
  }
}

async function saveNoteService(context: AppStateContext, event: any) {
  const { noteObject } = event.data;

  if (!navigator.onLine) {
    logger.warn({ note_id: noteObject.note_id }, 'savingNote: offline — queuing write to pendingWrites');
    await writePendingWrite({ id: noteObject.note_id, type: 'saveNote', payload: noteObject });
    await writeNoteToClientDatabase({ noteObject });
    return { savedNote: noteObject, queued: true };
  }

  // If the note's creation is still pending (POST /note hasn't succeeded yet),
  // don't attempt a PUT — the note doesn't exist in the DB and the request will
  // fail with a 500/404. Instead, merge the latest body into the pending createNote
  // entry so it's sent correctly when the queue flushes.
  try {
    const pendingWrites = await readPendingWrites();
    const pendingCreate = pendingWrites.find(
      (w: any) => w.id === noteObject.note_id && w.type === 'createNote'
    );
    if (pendingCreate) {
      logger.info(
        { note_id: noteObject.note_id },
        'savingNote: note has a pending createNote write — merging body and skipping PUT'
      );
      const mergedPayload = {
        ...pendingCreate.payload,
        noteObject: { ...pendingCreate.payload.noteObject, ...noteObject },
      };
      await writePendingWrite({ id: noteObject.note_id, type: 'createNote', payload: mergedPayload });
      await writeNoteToClientDatabase({ noteObject });
      return { savedNote: noteObject, queued: true };
    }
  } catch (pendingReadError: any) {
    logger.warn({ note_id: noteObject.note_id, error: pendingReadError?.message }, 'savingNote: failed to read pending writes — proceeding with save attempt');
  }

  try {
    const savedNote = await saveNoteCloud({ noteObject });
    await writeNoteToClientDatabase({ noteObject: savedNote });
    return { savedNote, queued: false };
  } catch (error: any) {
    logger.warn({ note_id: noteObject.note_id, error: error?.message }, 'savingNote: API error — queuing write to pendingWrites');
    await writePendingWrite({ id: noteObject.note_id, type: 'saveNote', payload: noteObject });
    await writeNoteToClientDatabase({ noteObject });
    return { savedNote: noteObject, queued: true };
  }
}

async function openNoteService(context: AppStateContext, event: any) {
  const { note: eventNote } = event.data;
  const noteId = eventNote?.note_id;
  const noteFromContext = context.notes?.[noteId];
  const note = { ...noteFromContext, ...eventNote };

  logger.info({ noteId }, 'openingNote: persisting to cloud and IndexedDB');

  const { selectedAreaOfFocus } = context;

  if (!selectedAreaOfFocus || !Object.keys(selectedAreaOfFocus).length) {
    logger.warn({ noteId }, 'openingNote: no selectedAreaOfFocus, skipping cloud/IDB sync');
    return { note, updatedAreaOfFocus: null };
  }

  const openNotesLength = selectedAreaOfFocus.order_of_items?.length || 0;
  const AOFObject = { ...selectedAreaOfFocus, order: openNotesLength } as any;

  const result = await addNoteToAreaOfFocusCollection({
    collectionId: selectedAreaOfFocus.area_of_focus_collection_id,
    noteId,
    isSelectedAreaOfFocus: true,
    AOFObject,
  });

  const updatedAreaOfFocus = result.result;
  await writeAreaOfFocus({ areaOfFocus: updatedAreaOfFocus });
  logger.info({ noteId }, 'openingNote: AOF persisted to cloud and IndexedDB');

  return { note, updatedAreaOfFocus };
}

async function closeNoteService(context: AppStateContext, event: any) {
  const { noteId } = event.data;
  const { selectedAreaOfFocus, notes } = context;

  const updatedAOFCollection = JSON.parse(JSON.stringify(selectedAreaOfFocus));
  const { noteIdToSelect } = setLastSelectedItemIdInCollection(updatedAOFCollection, noteId);

  await writeAreaOfFocus({ areaOfFocus: updatedAOFCollection });

  removeNoteFromAreaOfFocusCollection({
    collectionId: selectedAreaOfFocus.area_of_focus_collection_id,
    noteId,
  }).catch((error: any) => {
    logger.error({ error, noteId }, 'Failed to remove note from Area of Focus collection in cloud');
  });

  if (event.type === 'trashAndCloseNote') {
    trashNoteInCloud({ noteId }).catch((error: any) => {
      logger.error({ error, noteId }, 'closingNote: failed to trash note in cloud');
    });
  }

  const updatedAreasOfFocusCollections = {
    ...context.areasOfFocusCollections,
    [updatedAOFCollection.area_of_focus_collection_id]: updatedAOFCollection,
  };

  const updatedAreasOfFocusCollectionsIterable = Object.values(updatedAreasOfFocusCollections);
  const updatedAOFCollectionOrderOfItems = updatedAOFCollection.order_of_items;
  const updatedAOFCollectionOpenNoteObjects = updatedAOFCollectionOrderOfItems.length
    ? updatedAOFCollectionOrderOfItems.reduce((acc: any, id: string) => {
        acc[id] = notes?.[id];
        return acc;
      }, {})
    : {};

  return Promise.resolve({
    noteIdToSelect,
    updatedAOFCollection,
    updatedAOFCollectionOrderOfItems,
    updatedAOFCollectionOpenNoteObjects,
    updatedAreasOfFocusCollections,
    updatedAreasOfFocusCollectionsIterable,
  });
}

async function fetchNoteService(context: AppStateContext, event: any) {
  const { noteId: selectedNoteId } = event.data;
  const { defaultNotebook, notes } = context;

  if (!selectedNoteId) return Promise.reject();

  let note = notes && notes[selectedNoteId];
  if (note) {
    logger.debug({ noteId: selectedNoteId }, 'fetchingNote: Note now in context, using it');
    return { note };
  }

  if (!navigator.onLine) {
    logger.warn({ noteId: selectedNoteId }, 'fetchingNote: Note not available offline');
    return Promise.reject(new Error('Note not available offline'));
  }

  logger.info({ noteId: selectedNoteId }, 'fetchingNote: Fetching note from cloud');
  note = await fetchNoteFromCloud({ noteId: selectedNoteId });

  if (note && !note.notebook_id) {
    note.notebook_id = defaultNotebook!.notebook_id;
    const savedNote = await saveNoteCloud({ noteObject: note });
    return { note: savedNote };
  }

  return { note };
}

async function persistNoteSelectionService(context: AppStateContext) {
  const { selectedNoteId, selectedAreaOfFocus } = context;

  if (!selectedAreaOfFocus || !Object.keys(selectedAreaOfFocus).length || !selectedNoteId) {
    logger.warn({ selectedNoteId }, 'persistNoteSelection: missing AOF or noteId, skipping');
    return {};
  }

  logger.info(
    { selectedNoteId, collectionId: selectedAreaOfFocus.area_of_focus_collection_id },
    'persistNoteSelection: updating selected_stack in cloud and IndexedDB'
  );

  const result = await addNoteToAreaOfFocusCollection({
    collectionId: selectedAreaOfFocus.area_of_focus_collection_id,
    noteId: selectedNoteId,
    isSelectedAreaOfFocus: true,
    selectedOnly: true,
    AOFObject: { ...selectedAreaOfFocus } as any,
  });

  const updatedAreaOfFocus = result.result;
  const mergedAreaOfFocus = {
    ...updatedAreaOfFocus,
    order_of_items: context.selectedAreaOfFocus?.order_of_items ?? updatedAreaOfFocus.order_of_items,
  };
  await writeAreaOfFocus({ areaOfFocus: mergedAreaOfFocus });

  logger.info({ selectedNoteId }, 'persistNoteSelection: selected_stack persisted');
  return { updatedAreaOfFocus: mergedAreaOfFocus };
}

async function flushPendingWritesService() {
  const pending = await readPendingWrites();
  if (!pending.length) return { flushed: [], stillPending: [] };

  logger.info({ count: pending.length }, 'flushingPendingWrites: flushing queue');
  const flushed: any[] = [];
  const stillPending: string[] = [];

  for (const item of pending) {
    if (item.type === 'saveNote') {
      try {
        const savedNote = await saveNoteCloud({ noteObject: item.payload });
        await writeNoteToClientDatabase({ noteObject: savedNote });
        await deletePendingWrite({ id: item.id });
        flushed.push(savedNote);
      } catch (error) {
        logger.error({ error, id: item.id }, 'flushingPendingWrites: failed to flush saveNote item');
        stillPending.push(item.id);
      }
    } else if (item.type === 'createNote') {
      try {
        const { noteObject, selectedAreasOfFocusCollection, openNotesLength, isSelectedAreaOfFocus } = item.payload;
        const result = await createNote({ noteObject, selectedAreasOfFocusCollection, openNotesLength, isSelectedAreaOfFocus });
        await writeNoteToClientDatabase({ noteObject: result.note });
        await deletePendingWrite({ id: item.id });
        flushed.push(result.note);
      } catch (error) {
        logger.error({ error, id: item.id }, 'flushingPendingWrites: failed to flush createNote item');
        stillPending.push(item.id);
      }
    }
  }

  return { flushed, stillPending };
}

// ---------------------------------------------------------------------------
// Idle event routing — slice of idle.on for notes
// ---------------------------------------------------------------------------

export const notesIdleOn = {
  createNote: { target: 'creatingNote' },
  CREATE_NOTE: { target: 'creatingNote' },
  saveNote: { target: 'savingNote' },
  openNote: { target: 'openingNote' },
  selectNote: { target: 'selectingNote' },
  closeAndSelectNextNote: { target: 'closingNote' },
  FLUSH_PENDING_WRITES: { target: 'flushingPendingWrites' },
};

// ---------------------------------------------------------------------------
// Top-level event handlers (active from any child state)
// ---------------------------------------------------------------------------

export const notesTopLevelOn = {
  SYNC_NOTES: {
    actions: assign<AppStateContext, any>((context, event) => {
      if (!event.notes || !Object.keys(event.notes).length) return {};
      const mergedNotes = { ...context.notes, ...event.notes };
      const { selectedNoteId, selectedNote } = context;
      const syncedSelectedNote = selectedNoteId && event.notes[selectedNoteId];

      if (syncedSelectedNote) {
        const editorNoteId = selectedNote?.note_id;
        if (editorNoteId && editorNoteId !== selectedNoteId) {
          logger.warn({ editorNoteId, selectedNoteId }, 'SYNC_NOTES: selectedNote.note_id does not match selectedNoteId — correcting');
          interactionLedger.log(
            'noteSync_mismatch', 'stateSync',
            { editorNoteId, selectedNoteId, source: 'SYNC_NOTES' },
            `Editor showed note ${editorNoteId} but selectedNoteId was ${selectedNoteId} — fixed by SYNC_NOTES`
          );
        } else {
          logger.info({ selectedNoteId, newCount: Object.keys(event.notes).length }, 'SYNC_NOTES: merging background-synced notes into context, updating selectedNote');
        }
        // Use date_modified to pick the winning content version. Local is newer
        // when a debounced save is in-flight; server is newer when another device
        // has saved since the last sync. Metadata (notebook, labels, etc.) always
        // comes from the server snapshot.
        const localModified = selectedNote?.date_modified ? new Date(selectedNote.date_modified).getTime() : 0;
        const serverModified = syncedSelectedNote.date_modified ? new Date(syncedSelectedNote.date_modified).getTime() : 0;
        const localWins = localModified >= serverModified;
        logger.info({ selectedNoteId, localModified, serverModified, localWins }, 'SYNC_NOTES: resolving selected note content by date_modified');
        const preservedSelectedNote = localWins
          ? {
              ...syncedSelectedNote,
              note_title: selectedNote?.note_title ?? syncedSelectedNote.note_title,
              note_body: selectedNote?.note_body ?? syncedSelectedNote.note_body,
              note_snippet: selectedNote?.note_snippet ?? syncedSelectedNote.note_snippet,
              cursorPosition: selectedNote?.cursorPosition ?? syncedSelectedNote.cursorPosition,
            }
          : syncedSelectedNote;
        const finalMergedNotes = { ...mergedNotes, [selectedNoteId as string]: preservedSelectedNote };
        return { notes: finalMergedNotes, notesList: Object.values(finalMergedNotes), selectedNote: preservedSelectedNote };
      }

      logger.info({ newCount: Object.keys(event.notes).length }, 'SYNC_NOTES: merging background-synced notes into context');
      return { notes: mergedNotes, notesList: Object.values(mergedNotes) };
    }),
  },

  updateNoteTitle: {
    actions: assign<AppStateContext, any>((context, event) => {
      const { noteID, noteTitle } = event.data;
      const { notes, selectedNote, notebooks } = context;
      const updatedNote = { ...selectedNote, note_title: noteTitle, date_modified: new Date().toISOString() };
      const updatedNotes = { ...notes, [noteID]: updatedNote };
      const { notebookTreeList } = notebookTreeHelper({ notebooks, notes: updatedNotes });
      return {
        notes: updatedNotes,
        notesList: Object.values(updatedNotes),
        notebookTreeList,
        selectedNote: updatedNote,
        applicationTitle: noteTitle,
      };
    }),
  },

  updateNoteBody: {
    actions: assign<AppStateContext, any>((context, event) => {
      const { noteObject } = event.data;
      const { notes, selectedNote, selectedNoteId } = context;

      // Only update context for the currently selected note. Stale cross-note
      // saves (debounced flush after switching) are silently dropped here —
      // savingNote.onDone handles updating the notes dict for those.
      if (!noteObject || noteObject.note_id !== selectedNoteId || !selectedNote) return {};

      const fields: Partial<Note> = {};
      if (noteObject.note_body !== undefined) fields.note_body = noteObject.note_body;
      if (noteObject.note_snippet !== undefined) fields.note_snippet = noteObject.note_snippet;
      if (noteObject.cursorPosition != null) fields.cursorPosition = noteObject.cursorPosition;
      fields.date_modified = new Date().toISOString();

      if (!Object.keys(fields).length) return {};

      const updatedNote = { ...selectedNote, ...fields };
      return {
        selectedNote: updatedNote,
        notes: { ...notes, [noteObject.note_id]: updatedNote },
      };
    }),
  },

  trashAndCloseNote: {
    target: '.closingNote',
    actions: [(context: any, event: any) => {
      logger.info({ data: event.data }, 'trashAndCloseNote event received');
    }],
  },

  openNoteAndAddToAreaOfFocus: {
    target: '.openingNote',
    actions: [(context: any, event: any) => {
      logger.info({ data: event.data }, 'openNoteAndAddToAreaOfFocus event received');
    }],
  },
};

// ---------------------------------------------------------------------------
// State definitions
// ---------------------------------------------------------------------------

export const notesStates = {
  creatingNote: {
    entry: [(context: any, event: any) => {
      logger.debug({ context, event }, 'creatingNote data');
    }],
    initial: 'createNote',
    states: {
      createNote: {
        entry: [(context: any, event: any) => {
          logger.debug({ context, event }, 'createNote data');
        }],
        invoke: {
          id: 'creatingNote',
          src: createNoteService,
          onDone: {
            target: 'noteCreated',
            actions: [
              async (context: any, event: any) => {
                const { note } = event.data;
                try {
                  await writeNoteToClientDatabase({ noteObject: note });
                  logger.info({ note_id: note.note_id }, 'Note saved to IndexedDB after creation');
                } catch (error) {
                  logger.error({ error, note_id: note.note_id }, 'Failed to save note to IndexedDB');
                }
              },
              assign<AppStateContext, any>((context, event) => {
                const { note, queued } = event.data;
                const { notebooks } = context;
                const combinedNotes = { ...context.notes, [note.note_id]: note };
                const notesList = Object.values(combinedNotes) as Note[];
                const { notebookTreeList } = notebookTreeHelper({ notebooks, notes: combinedNotes });
                const selectedNotesNotebook = notebooks ? notebooks[note.notebook_id] : null;
                interactionLedger.log(
                  'noteCreate_addedToContext',
                  'stateMachine',
                  { note_id: note.note_id, queued, notes_count: notesList.length, trash: (note as any).trash },
                  `Note ${note.note_id} added to context.notes (queued=${queued}, trash=${(note as any).trash})`
                );
                return {
                  notes: combinedNotes,
                  notesList,
                  notebookTreeList,
                  selectedNotesNotebook,
                  applicationTitle: note.note_title,
                  noteSyncStatuses: { ...context.noteSyncStatuses, [note.note_id]: queued ? 'pending' : 'synced' },
                };
              }),
            ],
          },
          onError: {
            target: 'noteCreationFailed',
            actions: (context: any, event: any) => { return false; },
          },
        },
      },
      noteCreated: {
        type: 'parallel',
        states: {
          openNote: {
            initial: 'running',
            states: {
              running: {
                invoke: {
                  src: (context: any, event: any) => {
                    const { note } = event.data;
                    logger.debug({ context, event }, 'openNote data');
                    return new Promise((resolve, reject) => {
                      try {
                        const openNoteContext = openNoteHelper({ context, note });
                        return resolve({ ...openNoteContext });
                      } catch (err) {
                        logger.error({ err, note_id: note?.note_id }, 'openNote: openNoteHelper threw');
                        return reject(err);
                      }
                    });
                  },
                  onDone: {
                    target: 'complete',
                    actions: assign<AppStateContext, any>((context, event) => {
                      const { selectedNote, selectedNoteId } = event.data;
                      const { selectedAreaOfFocus, notes } = context;

                      interactionLedger.log(
                        'noteCreate_selectedNoteSet',
                        'stateMachine',
                        { selectedNoteId, note_id: (selectedNote as any)?.note_id, has_note_body: !!(selectedNote as any)?.note_body },
                        `selectedNote set after creation: ${selectedNoteId}`
                      );

                      // Optimistically add the new note to the open-notes tab list.
                      // addNoteToAreaOfFocus (parallel state) will overwrite this with
                      // the server result when the PUT succeeds. If the PUT fails for
                      // any reason, this ensures the tab still appears immediately.
                      const currentOrderOfItems = selectedAreaOfFocus?.order_of_items || [];
                      const updatedOrderOfItems =
                        selectedNoteId && !currentOrderOfItems.includes(selectedNoteId)
                          ? [...currentOrderOfItems, selectedNoteId]
                          : currentOrderOfItems;
                      const updatedOpenNoteObjects = updatedOrderOfItems.reduce((acc: any, id: string) => {
                        acc[id] = notes?.[id] || (id === selectedNoteId ? selectedNote : null);
                        return acc;
                      }, {});

                      return {
                        selectedNote,
                        selectedNoteId,
                        areaOfFocusOpenNoteIds: updatedOrderOfItems,
                        areaOfFocusOpenNoteObjects: updatedOpenNoteObjects,
                      };
                    }),
                  },
                  onError: {
                    target: 'complete',
                    actions: assign<AppStateContext, any>((context, event) => {
                      logger.error({ error: event.data }, 'openNote: failed to open note after creation');
                      interactionLedger.log(
                        'noteCreate_openNoteError',
                        'stateMachine',
                        { error: String(event.data), selectedNoteId: context.selectedNoteId },
                        `openNote FAILED after creation — selectedNote NOT updated`
                      );
                      return {};
                    }),
                  },
                },
              },
              complete: {
                type: 'final',
                entry: [(context: any, event: any) => {
                  logger.info({ context, event }, 'openNote complete data');
                }],
              },
            },
          },
          addNoteToAreaOfFocus: {
            initial: 'running',
            states: {
              running: {
                invoke: {
                  src: async (context: any, event: any) => {
                    const { isSelectedAreaOfFocus, note, openNotesLength, updatedAreaOfFocus: precomputedAOF } = event.data;
                    const noteId = note.note_id;

                    // The POST /note response already added the note to AOF atomically — use it.
                    if (precomputedAOF) {
                      interactionLedger.log(
                        'noteCreate_aofPrecomputed',
                        'stateMachine',
                        { noteId, aof_id: precomputedAOF?.area_of_focus_collection_id, order_of_items: precomputedAOF?.order_of_items },
                        `addNoteToAOF: using precomputed AOF from POST /note response`
                      );
                      await writeAreaOfFocus({ areaOfFocus: precomputedAOF });
                      return { updatedAreaOfFocus: precomputedAOF, ...event.data };
                    }

                    // Fallback: note was created offline/queued, or AOF add failed server-side.
                    // Try the separate PUT call.
                    const { selectedAreaOfFocus } = context;
                    interactionLedger.log(
                      'noteCreate_aofFallback',
                      'stateMachine',
                      { noteId, has_selectedAOF: !!(selectedAreaOfFocus && Object.keys(selectedAreaOfFocus).length), aof_id: selectedAreaOfFocus?.area_of_focus_collection_id ?? null },
                      `addNoteToAOF: no precomputed AOF — falling back to separate PUT`
                    );
                    if (selectedAreaOfFocus && Object.keys(selectedAreaOfFocus).length) {
                      const AOFObject = {
                        ...selectedAreaOfFocus,
                        order: openNotesLength || selectedAreaOfFocus.order_of_items?.length || 0,
                      } as any;
                      const result = await addNoteToAreaOfFocusCollection({
                        collectionId: selectedAreaOfFocus.area_of_focus_collection_id,
                        noteId,
                        isSelectedAreaOfFocus,
                        AOFObject,
                      });
                      const updatedAreaOfFocus = result.result;
                      interactionLedger.log(
                        'noteCreate_aofPutResult',
                        'stateMachine',
                        { noteId, has_result: !!updatedAreaOfFocus, order_of_items: updatedAreaOfFocus?.order_of_items ?? null },
                        `addNoteToAOF PUT result: has_aof=${!!updatedAreaOfFocus}`
                      );
                      if (updatedAreaOfFocus) {
                        await writeAreaOfFocus({ areaOfFocus: updatedAreaOfFocus });
                      }
                      return { updatedAreaOfFocus, ...event.data };
                    }
                    interactionLedger.log(
                      'noteCreate_aofRejecting',
                      'stateMachine',
                      { noteId },
                      `addNoteToAOF: no selectedAreaOfFocus — rejecting (note added to context but not to AOF)`
                    );
                    return Promise.reject({ data: event.data });
                  },
                  onDone: {
                    target: 'complete',
                    actions: assign<AppStateContext, any>((context, event) => {
                      const { updatedAreaOfFocus } = event.data;
                      if (!updatedAreaOfFocus) return {};
                      const updatedAreasOfFocusCollections = {
                        ...context.areasOfFocusCollections,
                        [updatedAreaOfFocus.area_of_focus_collection_id]: updatedAreaOfFocus,
                      };
                      const areasOfFocusCollectionsList = Object.values(updatedAreasOfFocusCollections) as AOFCollection[];
                      const updatedOrderOfItems = updatedAreaOfFocus.order_of_items || [];
                      const updatedOpenNoteObjects = updatedOrderOfItems.reduce((acc: any, id: string) => {
                        acc[id] = context.notes?.[id] || null;
                        return acc;
                      }, {});
                      interactionLedger.log(
                        'noteCreate_aofContextUpdate',
                        'stateMachine',
                        { aof_id: updatedAreaOfFocus.area_of_focus_collection_id, order_of_items: updatedOrderOfItems, open_note_count: updatedOrderOfItems.length },
                        `AOF context updated after note creation: ${updatedOrderOfItems.length} open notes`
                      );
                      return {
                        selectedAreaOfFocus: updatedAreaOfFocus,
                        areasOfFocusCollections: updatedAreasOfFocusCollections,
                        areasOfFocusCollectionsIterable: areasOfFocusCollectionsList,
                        areaOfFocusOpenNoteIds: updatedOrderOfItems,
                        areaOfFocusOpenNoteObjects: updatedOpenNoteObjects,
                      };
                    }),
                  },
                  onError: {
                    target: 'complete',
                    actions: assign<AppStateContext, any>((context, event) => {
                      logger.error({ error: event.data }, 'addNoteToAreaOfFocus: failed to add note to AOF');
                      interactionLedger.log(
                        'noteCreate_aofError',
                        'stateMachine',
                        { error: String(event.data) },
                        `addNoteToAOF ERROR — note still in context but not in open AOF tab`
                      );
                      return {};
                    }),
                  },
                },
              },
              complete: {
                type: 'final',
                entry: [assign<AppStateContext, any>((context, event) => {
                  logger.info({ context, event }, 'addNoteToAreaOfFocus complete data');
                  return {};
                })],
              },
            },
          },
        },
        onDone: {
          target: '#validatePostNoteCreation',
          entry: [assign<AppStateContext, any>((context, event) => {
            logger.info({ context, event }, 'noteCreated onDone data');
            interactionLedger.log(
              'noteCreate_complete',
              'stateMachine',
              {
                selectedNoteId: context.selectedNoteId,
                selectedNote_id: (context.selectedNote as any)?.note_id ?? null,
                has_note_body: !!(context.selectedNote as any)?.note_body,
                notes_count: context.notes ? Object.keys(context.notes).length : 0,
                aof_order_of_items: context.selectedAreaOfFocus?.order_of_items ?? null,
              },
              `noteCreated complete → idle: selectedNoteId=${context.selectedNoteId} note_body=${!!(context.selectedNote as any)?.note_body}`
            );
            return {};
          })],
        },
      },
      noteCreationFailed: {
        entry: [(context: any, event: any) => {
          logger.info({ context, event }, 'noteCreationFailed data');
        }],
        always: { target: '#idle' },
      },
      validatePostNoteCreation: {
        id: 'validatePostNoteCreation',
        always: [{ target: '#idle', cond: () => true }],
      },
    },
  },

  openingNote: {
    id: 'openingNote',
    entry: [
      (context: any, event: any) => {
        const noteId = event.data?.note?.note_id;
        interactionLedger.log('noteOpen', 'stateMachine', { noteId, source: 'openingNote' }, `Opening note: ${noteId}`);
      },
      assign<AppStateContext, any>((context, event) => {
        const { note: eventNote } = event.data;
        const noteId = eventNote?.note_id;
        if (!noteId) return {};

        const note = context.notes?.[noteId] || eventNote;
        const { selectedAreaOfFocus, notes, notebooks } = context;
        const currentOrderOfItems = selectedAreaOfFocus?.order_of_items || [];
        const updatedOrderOfItems = currentOrderOfItems.includes(noteId)
          ? currentOrderOfItems
          : [...currentOrderOfItems, noteId];
        const updatedOpenNoteObjects = updatedOrderOfItems.reduce((acc: any, id: string) => {
          acc[id] = notes?.[id] || (id === noteId ? note : null);
          return acc;
        }, {});

        return {
          selectedNote: note,
          selectedNoteId: noteId,
          selectedNotesNotebook: notebooks?.[note.notebook_id],
          applicationTitle: note.note_title,
          areaOfFocusOpenNoteIds: updatedOrderOfItems,
          areaOfFocusOpenNoteObjects: updatedOpenNoteObjects,
        };
      }),
    ],
    initial: 'openNote',
    states: {
      openNote: {
        invoke: {
          src: openNoteService,
          onDone: {
            target: '#idle',
            actions: assign<AppStateContext, any>((context, event) => {
              const { note, updatedAreaOfFocus } = event.data;
              if (!updatedAreaOfFocus) return {};
              const { notes, areasOfFocusCollections } = context;
              const updatedOrderOfItems = updatedAreaOfFocus.order_of_items || [];
              const updatedOpenNoteObjects = updatedOrderOfItems.reduce((acc: any, id: string) => {
                acc[id] = notes?.[id] || (id === note.note_id ? note : null);
                return acc;
              }, {});
              const updatedAreasOfFocusCollections = {
                ...areasOfFocusCollections,
                [updatedAreaOfFocus.area_of_focus_collection_id]: updatedAreaOfFocus,
              };
              return {
                selectedAreaOfFocus: updatedAreaOfFocus,
                areaOfFocusOpenNoteIds: updatedOrderOfItems,
                areaOfFocusOpenNoteObjects: updatedOpenNoteObjects,
                areasOfFocusCollections: updatedAreasOfFocusCollections,
                areasOfFocusCollectionsIterable: Object.values(updatedAreasOfFocusCollections),
              };
            }),
          },
          onError: {
            target: '#idle',
            actions: (context: any, event: any) => {
              logger.error({ event }, 'openingNote: failed to persist note to AOF');
              interactionLedger.log(
                'noteOpenError', 'stateMachine',
                { noteId: context.selectedNoteId, error: event.data?.message },
                `Failed to persist note open to AOF: ${event.data?.message}`
              );
            },
          },
        },
      },
    },
  },

  closingNote: {
    id: 'closingNote',
    entry: [
      (context: any, event: any) => {
        logger.info({ event }, 'appState: Entered closingNote state');
      },
      assign<AppStateContext, any>((context, event) => {
        const { noteId } = event.data;
        const { selectedAreaOfFocus, notes } = context;
        if (!selectedAreaOfFocus || !noteId) return {};

        const updatedAOFCollection = JSON.parse(JSON.stringify(selectedAreaOfFocus));
        const { noteIdToSelect } = setLastSelectedItemIdInCollection(updatedAOFCollection, noteId);
        const updatedOrderOfItems = updatedAOFCollection.order_of_items || [];
        const updatedOpenNoteObjects = updatedOrderOfItems.reduce((acc: any, id: string) => {
          acc[id] = notes?.[id] || null;
          return acc;
        }, {});

        const selectedNoteUpdate = noteIdToSelect
          ? { selectedNoteId: noteIdToSelect, selectedNote: notes?.[noteIdToSelect] || null }
          : { selectedNoteId: null, selectedNote: null };

        let notesUpdate = {};
        if (event.type === 'trashAndCloseNote') {
          const updatedNotes = { ...notes };
          delete updatedNotes[noteId];
          notesUpdate = { notes: updatedNotes, notesList: Object.values(updatedNotes) };
        }

        return {
          ...selectedNoteUpdate,
          ...notesUpdate,
          areaOfFocusOpenNoteIds: updatedOrderOfItems,
          areaOfFocusOpenNoteObjects: updatedOpenNoteObjects,
          selectedAreaOfFocus: updatedAOFCollection,
        };
      }),
    ],
    initial: 'closingNote',
    states: {
      closingNote: {
        invoke: {
          src: closeNoteService,
          onDone: [
            {
              target: '#checkAndSelectNextNote',
              actions: [assign<AppStateContext, any>((context, event) => {
                const {
                  updatedAOFCollection,
                  updatedAreasOfFocusCollections,
                  updatedAOFCollectionOrderOfItems,
                  updatedAOFCollectionOpenNoteObjects,
                  updatedAreasOfFocusCollectionsIterable,
                } = event.data;
                return {
                  areaOfFocusOpenNoteIds: updatedAOFCollectionOrderOfItems,
                  areaOfFocusOpenNoteObjects: updatedAOFCollectionOpenNoteObjects,
                  selectedAreaOfFocus: updatedAOFCollection,
                  areasOfFocusCollections: updatedAreasOfFocusCollections,
                  areasOfFocusCollectionsIterable: updatedAreasOfFocusCollectionsIterable,
                };
              })],
            },
          ],
          onError: {
            target: '#idle',
            actions: (context: any, event: any) => {
              logger.error({ event }, 'closingNote: failed to remove note from AOF collection');
            },
          },
        },
      },
      checkAndSelectNextNote: {
        id: 'checkAndSelectNextNote',
        invoke: {
          src: async (context: any, event: any) => {
            const { noteIdToSelect } = event.data;
            const { notes } = context;
            const selectedNote = notes[noteIdToSelect] || null;
            const selectedNoteId = selectedNote ? noteIdToSelect : null;
            return { selectedNote, selectedNoteId };
          },
          onDone: [
            {
              target: '#selectingNote',
              cond: (context: any, event: any) => {
                const { selectedNote, selectedNoteId } = event.data;
                return selectedNote !== null && selectedNoteId !== null;
              },
              actions: assign<AppStateContext, any>((context, event) => {
                const { selectedNote, selectedNoteId } = event.data;
                return { selectedNote, selectedNoteId };
              }),
            },
            {
              target: '#idle',
              actions: assign<AppStateContext, any>((context, event) => {
                const { selectedNote, selectedNoteId } = event.data;
                return { selectedNote, selectedNoteId };
              }),
            },
          ],
          onError: {
            target: '#idle',
            actions: (context: any, event: any) => {
              logger.error({ event }, 'checkAndSelectNextNote: failed');
            },
          },
        },
      },
    },
  },

  selectingNote: {
    id: 'selectingNote',
    entry: [
      (context: any, event: any) => {
        const noteId = event.data?.noteId ?? context.selectedNoteId;
        const inContext = !!(context.notes?.[noteId]);
        const editorNoteId = context.selectedNote?.note_id;
        if (editorNoteId && context.selectedNoteId && editorNoteId !== context.selectedNoteId) {
          logger.warn(
            { editorNoteId, selectedNoteId: context.selectedNoteId, incomingNoteId: noteId },
            'selectingNote: detected selectedNote/selectedNoteId mismatch on entry'
          );
          interactionLedger.log(
            'noteSync_mismatch', 'stateSync',
            { editorNoteId, selectedNoteId: context.selectedNoteId, incomingNoteId: noteId, source: 'selectingNote_entry' },
            `Editor note ${editorNoteId} ≠ selectedNoteId ${context.selectedNoteId} when selecting ${noteId}`
          );
        }
        interactionLedger.log('noteOpen', 'stateMachine', { noteId, source: 'selectingNote', inContext }, `Selecting note: ${noteId}`);
      },
      assign<AppStateContext, any>((context, event) => {
        const noteId = event.data?.noteId ?? context.selectedNoteId;
        const { notes } = context;
        const existingNote = notes && noteId && notes[noteId];

        if (existingNote) {
          logger.info({ noteId }, 'selectingNote: Note in context - instant selection');
          const combinedNotes = { ...notes, [noteId]: existingNote };
          return {
            selectedNoteId: noteId,
            selectedNote: existingNote,
            notes: combinedNotes,
            notesList: Object.values(combinedNotes),
            applicationTitle: existingNote.note_title || context.applicationTitle,
            showNoteSearch: false,
          };
        }

        logger.info({ noteId }, 'selectingNote: Note not in context - fetching in background');
        return { selectedNoteId: noteId, selectedNote: null, showNoteSearch: false };
      }),
    ],
    always: [
      {
        target: 'persistNoteSelection',
        cond: (context: any) => {
          const { selectedNoteId, notes } = context;
          return !!(selectedNoteId && notes && notes[selectedNoteId] !== undefined);
        },
      },
      { target: 'fetchingNote' },
    ],
  },

  fetchingNote: {
    invoke: {
      id: 'fetchingNote',
      src: fetchNoteService,
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          const { note } = event.data;
          const { notes } = context;
          const combinedNotes = { ...notes, [note.note_id]: note };
          return {
            notes: combinedNotes,
            notesList: Object.values(combinedNotes),
            applicationTitle: note.note_title,
            selectedNote: note,
            selectedNoteId: note.note_id,
          };
        }),
      },
      onError: {
        target: 'noteSelectionFailed',
        actions: (context: any, event: any) => {
          logger.error({ context, event }, 'Note selection failed');
          interactionLedger.log(
            'noteOpenError', 'stateMachine',
            { noteId: context.selectedNoteId, error: event.data?.message },
            `Note fetch failed: ${event.data?.message}`
          );
        },
      },
    },
  },

  noteSelectionFailed: {
    entry: [(context: any, event: any) => {
      logger.info({ context, event }, 'noteSelectionFailed data');
    }],
    always: { target: '#idle' },
  },

  persistNoteSelection: {
    id: 'persistNoteSelection',
    invoke: {
      src: persistNoteSelectionService,
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          const { updatedAreaOfFocus } = event.data || {};
          if (!updatedAreaOfFocus) return {};

          const mergedAreaOfFocus = {
            ...updatedAreaOfFocus,
            order_of_items: context.selectedAreaOfFocus?.order_of_items ?? updatedAreaOfFocus.order_of_items,
          };
          const updatedAreasOfFocusCollections = {
            ...context.areasOfFocusCollections,
            [mergedAreaOfFocus.area_of_focus_collection_id]: mergedAreaOfFocus,
          };
          return {
            selectedAreaOfFocus: mergedAreaOfFocus,
            areasOfFocusCollections: updatedAreasOfFocusCollections,
            areasOfFocusCollectionsIterable: Object.values(updatedAreasOfFocusCollections),
          };
        }),
      },
      onError: {
        target: '#idle',
        actions: (context: any, event: any) => {
          logger.error({ event }, 'persistNoteSelection: failed to persist note selection to cloud/IDB');
          interactionLedger.log(
            'noteOpenError', 'stateMachine',
            { noteId: context.selectedNoteId, error: event.data?.message },
            `persistNoteSelection failed: ${event.data?.message}`
          );
        },
      },
    },
  },

  savingNote: {
    id: 'savingNote',
    initial: 'saving',
    entry: [assign<AppStateContext, any>((context, event) => {
      const noteId = event.data?.noteObject?.note_id;
      if (!noteId) return {};
      return { noteSyncStatuses: { ...context.noteSyncStatuses, [noteId]: 'saving' } };
    })],
    states: {
      saving: {
        invoke: {
          src: saveNoteService,
          onDone: {
            target: '#idle',
            actions: assign<AppStateContext, any>((context, event) => {
              const { savedNote, queued } = event.data;
              const { notes, selectedNote: currentNote, notebooks } = context;
              const isCurrentNote = currentNote?.note_id === savedNote.note_id;
              const syncStatus = queued ? 'pending' : 'synced';

              if (!isCurrentNote) {
                logger.warn(
                  { savedNoteId: savedNote.note_id, currentNoteId: currentNote?.note_id },
                  'savingNote.onDone: stale save completed for a note that is no longer selected'
                );
                interactionLedger.log(
                  'stale_note_save', 'stateSync',
                  { savedNoteId: savedNote.note_id, currentNoteId: currentNote?.note_id },
                  `Stale save for ${savedNote.note_id} completed after switching to ${currentNote?.note_id}`
                );
                const updatedNotes = { ...notes, [savedNote.note_id]: savedNote };
                const notesList = Object.values(updatedNotes) as Note[];
                const { notebookTreeList } = notebookTreeHelper({ notebooks, notes: updatedNotes });
                return { notes: updatedNotes, notesList, notebookTreeList, noteSyncStatuses: { ...context.noteSyncStatuses, [savedNote.note_id]: syncStatus } };
              }

              // Preserve in-memory title and cursor position from currentNote.
              // The user may have moved the cursor (or continued typing a title)
              // while the async save was in-flight; taking those values from the
              // server response would silently clobber the latest local state.
              const mergedNote = {
                ...savedNote,
                note_title: currentNote!.note_title,
                cursorPosition: currentNote!.cursorPosition ?? savedNote.cursorPosition,
              };
              const updatedNotes = { ...notes, [savedNote.note_id]: mergedNote };
              const notesList = Object.values(updatedNotes) as Note[];
              const { notebookTreeList } = notebookTreeHelper({ notebooks, notes: updatedNotes });
              return { notes: updatedNotes, notesList, notebookTreeList, selectedNote: mergedNote, noteSyncStatuses: { ...context.noteSyncStatuses, [savedNote.note_id]: syncStatus } };
            }),
          },
          onError: {
            target: '#savingNoteFailure',
            actions: (context: any, event: any) => {
              logger.error({ error: event.data }, 'Failed to save note');
            },
          },
        },
      },
    },
  },

  savingNoteFailure: {
    id: 'savingNoteFailure',
    entry: [(context: any, event: any) => {
      logger.info({ context, event }, 'savingNoteFailure data');
    }],
    always: { target: '#idle' },
  },

  flushingPendingWrites: {
    id: 'flushingPendingWrites',
    invoke: {
      src: flushPendingWritesService,
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          const { flushed, stillPending } = event.data;
          if (!flushed.length && !stillPending.length) return {};
          const updatedNotes = { ...context.notes };
          const syncStatusUpdates: Record<string, 'synced' | 'pending'> = {};
          flushed.forEach((note: any) => {
            updatedNotes[note.note_id] = note;
            syncStatusUpdates[note.note_id] = 'synced';
          });
          stillPending.forEach((id: string) => { syncStatusUpdates[id] = 'pending'; });
          logger.info({ flushedCount: flushed.length, stillPendingCount: stillPending.length }, 'flushingPendingWrites: merged results into context');
          return { notes: updatedNotes, notesList: Object.values(updatedNotes), noteSyncStatuses: { ...context.noteSyncStatuses, ...syncStatusUpdates } };
        }),
      },
      onError: {
        target: '#idle',
        actions: (context: any, event: any) => {
          logger.error({ error: event.data }, 'flushingPendingWrites: unexpected error');
        },
      },
    },
  },
};
